/**
 * Работа с датами
 * @packageDocumentation
 */

import Core from '@/core/core/core';
import { useI18n } from '@/core/plugins/i18n';

/**
 * Получить день недели
 */
export const getDayOfWeek = (y: number, m: number, d: number): string => {
	const i18nCalendar = useI18n().Calendar;

	switch ((new Date(y, --m, d)).getDay()) {
		case 0:
			return i18nCalendar.Dw_1 + '.';
		case 1:
			return i18nCalendar.Dw_2 + '.';
		case 2:
			return i18nCalendar.Dw_3 + '.';
		case 3:
			return i18nCalendar.Dw_4 + '.';
		case 4:
			return i18nCalendar.Dw_5 + '.';
		case 5:
			return i18nCalendar.Dw_6 + '.';
		case 6:
			return i18nCalendar.Dw_7 + '.';
		default:
			return '';
	}
};

/**
 * Преобразовать дату в строку формата Y-m-d H:i:s
 * @param dateObject - объект Date
 * @param langFormat - если true, формат даты будет взят из Core.state.dateFormat
 * @param timeFormat - 0-только дата | 1-дата и часы | 2-дата и часы с минутами | 3-дата и часы с минутами и секундами
 */
export const dateToString = (dateObject: Date, langFormat: boolean = true, timeFormat: 0 | 1 | 2 | 3 = 2): string => {
	let Y = String(dateObject.getFullYear());
	let d = String(dateObject.getDate()).padStart(2, '0');
	let m = String(dateObject.getMonth() + 1).padStart(2, '0');
	let H = String(dateObject.getHours()).padStart(2, '0');
	let i = String(dateObject.getMinutes()).padStart(2, '0');
	let s = String(dateObject.getSeconds()).padStart(2, '0');

	let date = Y + '-' + m + '-' + d;
	if (timeFormat >= 1) date += ' ' + H;
	if (timeFormat >= 2) date += ':' + i;
	if (timeFormat === 3) date += ':' + s;
	if (langFormat) date = dateFormat(date, timeFormat);

	return date;
};

/**
 * Получить объект Date из строки
 * @param date - строка с датой
 */
export function stringToDate(date: string): Date {
	if (date.indexOf('T') === -1) {
		if (date.length == 10) {
			date += ' 00:00:00';
		}

		date = date.replace(' ', 'T') + Core.state.gmt;
	}

	return new Date(date);
}

/**
 * Генерация даты-строки из миллисекунд (UTC), в формате Y-m-d H:i:s
 * @param timestamp - дата в мс, если не передана, берется дата на момент вызова функции
 * @param useFormat - если true, формат даты будет взят из Core.state.dateFormat
 * @param time
 * - 0: только дата
 * - 1: дата часы
 * - 2: дата и часы с минутами
 * - 3: дата и часы с минутами и секундами
 * - auto: часы и минуты будут выводиться только, если они не равны нулю
 */
export function genDate(timestamp: number | undefined, useFormat: boolean = true, time: 0 | 1 | 2 | 3 | 'auto' = 0): string {
	if (!timestamp) timestamp = new Date().getTime();

	const dateObject = new Date(timestamp);

	const Y = dateObject.getFullYear();
	const m = String(dateObject.getMonth() + 1).padStart(2, '0');
	const d = dateObject.getDate().toString().padStart(2, '0');

	const H = dateObject.getHours().toString().padStart(2, '0');
	const i = dateObject.getMinutes().toString().padStart(2, '0');
	const s = dateObject.getSeconds().toString().padStart(2, '0');

	if (time === 'auto') {
		time = 0;
		if (dateObject.getHours()) time = 1;
		if (dateObject.getMinutes()) time = 2;
		if (dateObject.getSeconds()) time = 3;
	}

	let date = Y + '-' + m + '-' + d;

	if (time) date += ' ';
	if (time >= 1) date += H;
	if (time >= 2) date += ':' + i;
	if (time >= 3) date += ':' + s;

	if (useFormat) {
		date = dateFormat(dateObject, time);
	}

	return date;
}

/**
 * Генерация даты-строки (время московское) из миллисекунд (UTC), в формате Y-m-d H:i:s
 * @param ms - дата в мс, если не передана, берется дата на момент вызова функции
 * @param langFormat - если true, формат даты будет взят из Core.state.dateFormat
 */
export function genDateMoscow(ms: number | undefined, useFormat: boolean = true): string {
	const time_zone = (new Date().getTimezoneOffset() / 60) * (-1);
	const dateoffset = time_zone * 60 * 60 * 1000;
	const moscow_dateoffset = 3 * 60 * 60 * 1000;

	let date: Date;
	if (ms !== undefined) {
		date = new Date(ms);
	} else {
		date = new Date();
	}

	date.setTime(date.getTime() - dateoffset + moscow_dateoffset);

	let Y = String(date.getFullYear());
	let d = String(date.getDate()).padStart(2, '0');
	let m = String(date.getMonth() + 1).padStart(2, '0');
	let H = String(date.getHours()).padStart(2, '0');
	let i = String(date.getMinutes()).padStart(2, '0');
	let s = String(date.getSeconds()).padStart(2, '0');

	let dateStr = Y + '-' + m + '-' + d + ' ' + H + ':' + i + ':' + s;
	if (useFormat) {
		dateStr = dateFormat(dateStr);
	}

	return dateStr;
}

/**
 * Перевод даты в формат пользователя
 * @param dateMixed - объект даты или строка в формате Y-m-d H:i:s
 * @param time - 0-только дата | 1-дата и часы | 2-дата и часы с минутами | 3-дата и часы с минутами и секундами
 * @param dateFormat -
 * @param emptyValue - значение, которое надо вернуть, если дата не указана
 */
export function dateFormat(
	dateMixed: string | Date,
	time: 0 | 1 | 2 | 3 = 2,
	dateFormat?: string,
	emptyValue?: string,
): string {
	if (!emptyValue) emptyValue = useI18n().Common.Not_date;

	if (typeof dateMixed === 'string') {
		if (!dateMixed || dateMixed.substring(0, 10) === '0000-00-00') {
			if (emptyValue && emptyValue !== '0000-00-00') {
				return emptyValue;
			}
		}
	}

	if (!dateMixed) return emptyValue!;

	if (!dateFormat) {
		dateFormat = Core.state.dateFormat;
	}

	dateFormat = dateFormat.toLowerCase().replace('m', 'mm').replace('y', 'yy').replace('d', 'dd');

	let date: Date;
	if (dateMixed instanceof Date) {
		date = dateMixed;
	} else {
		let dateForObj = dateMixed.substring(0, 10);
		if (dateForObj.length === 7) dateForObj += '-01';
		dateForObj += ' 00:00';
		dateForObj = dateForObj.replace(/-/g, '/'); // safari fix

		date = new Date(dateForObj);
		if (isNaN(Number(date))) return emptyValue!;
	}

	let result = '';

	if (dateFormat && window['$']) {
		result = $.datepicker.formatDate(dateFormat, date);
	}

	if (time && typeof dateMixed === 'string' && dateMixed.length > 10 || time && dateMixed instanceof Date) {
		let timeStr: string = '';
		if (dateMixed instanceof Date) {
			timeStr = new Date().toLocaleTimeString('en-US', {
				hour12: false,
				hour: 'numeric',
				minute: time >= 2 ? 'numeric' : undefined,
				second: time >= 3 ? 'numeric' : undefined,
			});
		} else {
			switch (time) {
				// часы
				case 1:
					timeStr = dateMixed.substring(11, 13);

					break;
				// часы и минуты
				case 2:
					timeStr = dateMixed.substring(11, 16);

					break;
				// часы, минуты и секунды
				case 3:
					timeStr = dateMixed.substring(11, 19);

					break;
			}
		}

		result += ' ' + timeStr;
	}

	result = result.trim();

	return result;
}

/**
 * Перевод даты из формата пользователя в системный формат Y-m-d H:i:s
 * @param date - строка с датой
 * @param emptyValue - значение, которое надо вернуть, если дата не указана
 */
export function dateUnformat(date: string, emptyValue?: string): string {
	if (!emptyValue) emptyValue = useI18n().Common.Not_date;

	if (!date) {
		return emptyValue!;
	}

	if (date.match(/\d\d\d\d-\d\d-\d\d/)) {
		return date;
	}

	const dateFormat = Core.state.dateFormat.toLowerCase();

	const dateChunks = date.split(/\W/);
	const dateFormatChunks = dateFormat.split(/\W/);

	let d = '';
	let m = '';
	let y = '';

	dateFormatChunks.forEach((type, index) => {
		switch (type) {
			case 'd':
				d = dateChunks[index];

				break;
			case 'm':
				m = dateChunks[index];

				break;
			case 'y':
				y = dateChunks[index];

				break;
		}
	});

	let result = y + '-' + m + '-' + d;

	if (!result.match(/\d\d\d\d-\d\d-\d\d/)) {
		console.info('Неверный формат даты, будет возращена текущая дата, ' + result);

		result = genDate((new Date()).getTime(), false).substring(0, 10);
	}

	return result;
}
